import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import usePortalCustomEvent, { portalCustomEvents } from '../hooks/usePortalCustomEvent';
import { rewritePath } from '../utils/routeUtils';

const RacingCruncher = React.lazy(() => import('../scenes/myTeams/RacingCruncher'));
const RapidFire = React.lazy(() => import('../scenes/rapidFire/RapidFire'));
const RapidFireChallenge = React.lazy(() => import('../scenes/rapidFire/RapidFireChallenge'));
const About = React.lazy(() => import('../scenes/static/About'));
const AccountRouteHandler = React.lazy(() => import('../scenes/account/AccountRouteHandler'));
const Challenge = React.lazy(() => import('../scenes/Challenge'));
const ContactUs = React.lazy(() => import('../scenes/ContactUs'));
const LiveChallenges = React.lazy(() => import('../scenes/LiveChallenges'));
const Lobby = React.lazy(() => import('../scenes/Lobby'));
const GambleResponsibly = React.lazy(() => import('../scenes/static/GambleResponsibly'));
const FAQs = React.lazy(() => import('../scenes/static/FAQs'));
const SwapRulesPage = React.lazy(() => import('../scenes/static/SwapRulesPage'));
const RacingCruncherRules = React.lazy(() => import('../scenes/static/RacingCruncherRulesPage'));
const Friends = React.lazy(() => import('../scenes/Friends'));
const GlobalLeaderboard = React.lazy(() => import('../scenes/GlobalLeaderboard'));
const GiftRouteHandler = React.lazy(() => import('../scenes/GiftRouteHandler'));
const HowToPlay = React.lazy(() => import('../scenes/static/HowToPlay'));
const MyChallenges = React.lazy(() => import('../scenes/MyChallenges'));
const Trainer = React.lazy(() => import('../scenes/Trainer'));
const NotFound = React.lazy(() => import('../scenes/static/NotFound'));
const PrivacyPolicy = React.lazy(() => import('../scenes/static/PrivacyPolicy'));
const Rules = React.lazy(() => import('../scenes/static/Rules'));
const JackpotSlots = React.lazy(() => import('../scenes/JackpotSlots'));
const TermsAndConditions = React.lazy(() => import('../scenes/static/TermsAndConditions'));
const EnterAdditionalChallenges = React.lazy(() => import('../scenes/EnterAdditionalChallenges'));
const MyTeams = React.lazy(() => import('../scenes/MyTeams'));
const LoadCreateChallenge = React.lazy(() => import('../scenes/h2h/LoadCreateChallenge'));
const FantasyInsider = React.lazy(() => import('../scenes/fantasyInsider'));

const AppRouter = () => {
  const {
    displayComboCalculator,
    displayFantasyInsider,
    displayFriends,
    displayGiftStore,
    displayGlobalLeaderboard,
    displayJackpotSlots,
    displayMyTeams,
    displayTraining,
    enablePxc,
    freeOnly,
    rewriteRules
  } = useSelector(({ settings }) => settings);
  const history = useHistory();

  const rewrite = rewritePath(rewriteRules);

  // setting up a listener for portal event that pushes urls to client apps
  usePortalCustomEvent(
    portalCustomEvents.EVENT_PORTAL_PUSH_URL,
    React.useCallback(
      e => {
        if (!!e.url && e.url.startsWith('/fantasy')) {
          const clienturl = e.url.replace('/fantasy', '');

          history.push(clienturl);
        }
      },
      [history]
    )
  );

  return (
    <Switch>
      <Route exact path="/">
        <Redirect
          to={{
            pathname: '/lobby',
            search: history.location.search
          }}
        />
      </Route>
      <Route
        path={rewrite('/challenge/:id/:entryIds?')}
        render={props => <Challenge key={props.match.params.id} {...props} />}
      />
      <Route exact path="/lobby/:sport?/:category?" component={Lobby} />
      <Route
        exact
        path={rewrite('/live-challenges/:sport?/:category?')}
        component={LiveChallenges}
      />
      {displayGiftStore && <Route path="/gift" component={GiftRouteHandler} />}
      {displayFriends && <Route path="/groups" component={Friends} />}
      {displayGlobalLeaderboard && (
        <Route
          path="/ultimate-champion/:leaderboardName?/:sport?/:competition?"
          component={GlobalLeaderboard}
        />
      )}
      <Route path="/how-to-play" component={HowToPlay} />
      <Route path="/create-challenge" component={LoadCreateChallenge} />
      <Route
        path={rewrite('/rapidfire/challenge/:challengeId')}
        render={props => <RapidFireChallenge key={props.match.params.challengeId} {...props} />}
      />
      <Route path="/rapidfire/:competitionId?/:contestType?/:section?" component={RapidFire} />
      <Redirect exact path={rewrite('/my-challenges')} to={rewrite('/my-challenges/live')} />
      <Route exact path={rewrite('/my-challenges/:section/:sport?')} component={MyChallenges} />
      {displayComboCalculator && <Route path="/combo-calculator" component={RacingCruncher} />}
      {displayFantasyInsider && (
        <Route path="/fantasy-insider/:sport?" component={FantasyInsider} />
      )}
      {displayMyTeams && <Route path="/my-teams/:section?/:sport?" component={MyTeams} />}
      {displayTraining && <Route path={rewrite('/training/challenge/:id')} component={Challenge} />}
      {displayTraining && <Route exact path="/training/:sport?/:category?" component={Trainer} />}
      {displayJackpotSlots && <Route path="/spin-and-win" component={JackpotSlots} />}
      <Route
        path={rewrite('/enter-additional-challenges/:lineupId')}
        render={props => <EnterAdditionalChallenges isPxcEnabled={!!enablePxc} {...props} />}
      />
      <Route path="/account" component={AccountRouteHandler} />
      <Route path="/contact-us" component={ContactUs} />
      <Route path="/faqs/:section?" component={FAQs} />
      <Route path="/swap-rules" component={SwapRulesPage} />
      <Route path="/player_swap_rules">
        <Redirect to="/swap-rules" />
      </Route>
      <Route path="/combo-calculator-rules" component={RacingCruncherRules} />
      <Route path="/rules" component={Rules} />
      <Route path="/terms" component={TermsAndConditions} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/about" component={About} />
      {!freeOnly && <Route path="/gamble-responsibly" component={GambleResponsibly} />}
      <Route path="*" component={NotFound} />
    </Switch>
  );
};

export default AppRouter;
